export function notNullOrUndefined<TValue>(
  value: TValue | null | undefined
): value is TValue {
  return value !== null && value !== undefined;
}

export function keyOf<T>(k: keyof T) {
  return k;
}

export function nameof<T>(name: keyof T): keyof T {
  return name;
}

/** Union of primitives to skip with deep omit utilities. */
type Primitive =
  | string
  | Function
  | number
  | boolean
  | Symbol
  | undefined
  | null;

/** Deeply omit members of an array of interface or array of type. */
export type DeepOmitArray<T extends unknown[], K> = {
  [P in keyof T]: DeepOmit<T[P], K>;
};

/** Deeply omit members of an interface or type. */
export type DeepOmit<T, K> = T extends Primitive
  ? T
  : {
      [P in Exclude<keyof T, K>]: T[P] extends infer TP //extra level of indirection needed to trigger homomorhic behavior // distribute over unions
        ? TP extends Primitive
          ? TP // leave primitives and functions alone
          : TP extends unknown[]
          ? DeepOmitArray<TP, K> // Array special handling
          : DeepOmit<TP, K>
        : never;
    };

export type Rename<T, K extends keyof T, N extends string> = Pick<
  T,
  Exclude<keyof T, K>
> & { [P in N]: T[K] };

/**
 * Generic type for a type-safe setFieldValue function for forms.
 * Usage: SetFieldValue<FieldUnion, ValueType> where FieldUnion is a union of allowed field names.
 */
export type FormFieldValue<T = string> = T | T[] | null;
export type SetFieldValue<F extends string = string, V = string> = (
  field: F,
  value: FormFieldValue<V>,
  shouldValidate?: boolean
) => void;

export const enumValues = <Enum extends Record<string, number | string>>(
  e: Enum,
  stringOnly: boolean = true
) => {
  const values = Object.values(e);
  return stringOnly ? values.filter(value => typeof value === "string") : true;
};

declare global {
  namespace React {
    interface DOMAttributes<T> {
      onResize?: ReactEventHandler<T> | undefined;
      onResizeCapture?: ReactEventHandler<T> | undefined;
      nonce?: string | undefined;
    }
  }
}
